/* eslint-disable max-lines */
import { SkeletonMotion, ThemeTokens } from '@acp/core-ui';
import { AgencyPortfolioProperty } from '@acp/shared';
import {
    Badge,
    Button,
    Card,
    CardBody,
    Grid,
    GridItem,
    HStack,
    Image,
    Skeleton,
    Text,
    Tooltip,
    VStack,
} from '@chakra-ui/react';
import ReactTimeago from 'react-timeago';
import { format } from 'date-fns';
import { brandIcons } from '@acp/core-ui/assets';
import { NewManagementEnrolButton } from '../../../integrated-portfolio';

const gap = 4;

function GridWrapper({ children }: { children: React.ReactNode }) {
    return (
        <Grid
            gridTemplateRows="auto"
            gridTemplateAreas={[`
                "new-management new-management"
                "property property"
                "first-seen actions"
            `, null, null, '"new-management property first-seen actions"']}
            gridTemplateColumns={['1fr 1fr', null, null, '3fr 6fr 3fr 3fr']}
            gap={0}
            alignItems="center"
            justifyItems="start"
            h="auto"
        >
            {children}
        </Grid>
    );
}

export function NewManagementRowSkeleton() {
    return (
        <Card
            borderWidth={1}
            borderColor={ThemeTokens.Defaults.borderColor}
            bg="white"
            variant="filled"
            w="full"
            p={5}
        >
            <CardBody as={GridWrapper} p={0} alignItems="center" justifyItems="center">
                <GridItem as={VStack} gap={1} gridArea="new-management" mb={[gap, null, null, 0]} alignItems="start">
                    <Skeleton>
                        <Text fontSize="small">New Management</Text>
                    </Skeleton>
                    <Skeleton>
                        <Text fontSize="x-small">
                            From an integration
                        </Text>
                    </Skeleton>
                </GridItem>

                <VStack
                    alignItems="start"
                    gap={1}
                    w={52}
                    gridArea="property"
                    mb={[gap, null, null, 0]}
                >
                    <SkeletonMotion height={5} />
                    <SkeletonMotion height={5} />
                </VStack>

                <GridItem as={VStack} gridArea="first-seen" alignItems="start" justifySelf="start" gap={1} pl={[0, null, null, 0]} mb={[gap, 0]}>
                    <Skeleton>
                        <Text fontSize="xs">
                            First seen 3 weeks ago
                        </Text>
                    </Skeleton>
                </GridItem>

                <GridItem as={HStack} gridArea="actions" justifyContent="end" width="100%" gap={3} alignSelf={['end', null, null, 'center']}>
                    <Skeleton>
                        <Button
                            borderRightWidth={1}
                            borderRightColor="secondary.900"
                            color="secondary.25"
                            size="sm"
                        >
                            Action menu xx
                        </Button>
                    </Skeleton>
                </GridItem>
            </CardBody>
        </Card>
    );
}

export function NewManagementRow({
    agencyPortfolioProperty,
}: {
    agencyPortfolioProperty: AgencyPortfolioProperty;
}) {
    return (
        <Card
            borderColor={ThemeTokens.Defaults.borderColor}
            borderWidth={1}
            borderLeftWidth={6}
            borderLeftColor="secondary.500"
            bg="white"
            variant="filled"
            w="full"
            shadow="sm"
            p={5}
        >
            <CardBody as={GridWrapper} p={0} alignItems="center" justifyItems="center" rowGap={[2, null, null, 0]}>
                <GridItem as={HStack} gap={2} gridArea="new-management" mb={[gap, null, null, 0]}>
                    <Image src={brandIcons.newManagementSmMuted} h="35px" />
                    <VStack gap={0} alignItems="start">
                        <Text fontWeight="medium">New management</Text>
                        <Text fontSize="small" color="charcoal.500">
                            From
                            {' '}
                            {agencyPortfolioProperty.softwareName}
                        </Text>
                    </VStack>
                </GridItem>

                <GridItem gridArea="property" mb={[gap, null, null, 0]} pr={5}>
                    <Text fontWeight="medium">{agencyPortfolioProperty.address}</Text>
                </GridItem>

                <GridItem as={VStack} gridArea="first-seen" alignItems="start" justifySelf="start" gap={1} pl={[0, null, null, 0]}>
                    <Tooltip label={`First seen ${format(agencyPortfolioProperty.firstActiveDate, 'dd/MM/yyyy')}`} bgColor="white" color="black">
                        <Badge colorScheme="blackAlpha" variant="subtle" userSelect="none">
                            First seen
                            {' '}
                            <ReactTimeago
                                date={agencyPortfolioProperty.firstActiveDate}
                                title=""
                                live={false}
                            />
                        </Badge>
                    </Tooltip>
                </GridItem>

                <GridItem as={HStack} gridArea="actions" justifyContent="end" width="100%" gap={3} alignSelf={['end', null, null, 'center']}>
                    <NewManagementEnrolButton
                        isUnderEnrolment={agencyPortfolioProperty.isUnderEnrolment}
                        underEnrolmentAt={agencyPortfolioProperty.underEnrolmentAt}
                        underEnrolmentBy={agencyPortfolioProperty.underEnrolmentBy}
                        address={agencyPortfolioProperty.address}
                        portfolioPropertyId={agencyPortfolioProperty.portfolioPropertyId}
                    />
                </GridItem>
            </CardBody>
        </Card>
    );
}
